import React from 'react';
import { Box, IconButton } from '@chakra-ui/react';
import VtexComponent from './VtexComponent';
import { scrollBarStyle } from '../../utils/layoutConfig';
import { selectedPageObject, headerPageObject, footerPageObject } from '../../redux/pagesSlice';
import { selectedToggleValue } from '../../redux/canvasResolutionSlice';
import { useSelector, useDispatch } from 'react-redux';
import VtexFixedComponent from './VtexFixedComponent';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { findCompByIdRecursively, filterByMobileState } from '../../utils/functions';
import { selectComponent } from '../../redux/selectedComponentSlice';
import { UilDraggabledots } from '@iconscout/react-unicons';
import _, { isEmpty } from 'lodash';
import DebugBox from './DebugBox';
import CanvasElementPlaceholder from './CanvasElementPlaceholder';
import HoverOverlay from './HoverOverlay';
import { setHoverElement } from '../../redux/hoverElementSlice';

const RenderHeader = ({ pagesData, headerLayout, selectedCanvasResolution, selectedPageObjectValue }) => {
	if (
		selectedPageObjectValue?.pageType == 'footer' ||
		selectedPageObjectValue?.pageType == 'minicart' ||
		selectedPageObjectValue?.pageType == 'menu'
	)
		return null;
	if (
		headerLayout.length &&
		!pagesData.filter((e) => e.componentGroup === 'HeaderMobile' || e.componentGroup === 'HeaderDesktop').length
	) {
		const headerComp = headerLayout.filter(
			(el) => el.componentGroup === (selectedCanvasResolution > 1024 ? 'HeaderDesktop' : 'HeaderMobile'),
		)[0];

		return (
			<VtexFixedComponent
				className="VtexComponent"
				{...{
					comp: headerComp,
					componentId: headerComp.componentId,
					componentGroup: headerComp.componentGroup,
				}}
			/>
		);
	} else {
		return null;
	}
};

const RenderFooter = ({ pagesData, footerLayout, selectedCanvasResolution, selectedPageObjectValue }) => {
	if (
		selectedPageObjectValue?.pageType == 'header' ||
		selectedPageObjectValue?.pageType == 'minicart' ||
		selectedPageObjectValue?.pageType == 'menu'
	)
		return null;
	if (
		footerLayout.length &&
		!pagesData.filter((e) => e.componentGroup === 'FooterMobile' || e.componentGroup === 'FooterDesktop').length
	) {
		const footerComp = footerLayout.filter(
			(el) => el.componentGroup === (selectedCanvasResolution > 1024 ? 'FooterDesktop' : 'FooterMobile'),
		)[0];

		return (
			<VtexFixedComponent
				className="VtexComponent"
				{...{
					comp: footerComp,
					componentId: footerComp.componentId,
					componentGroup: footerComp.componentGroup,
				}}
			/>
		);
	} else {
		return null;
	}
};

function handleRightClick(e, selectedPageObjectValue, compProps, dispatch) {
	e.preventDefault();
	e.stopPropagation();
	const xPos = e.pageX + 'px';
	const yPos = e.pageY + 'px';
	const comp = findCompByIdRecursively(selectedPageObjectValue.pageData, compProps.componentId)?.comp;
	let childComp;
	let closest;
	if (e.ctrlKey) {
		closest = e.target.dataset.selection == compProps.componentId ? e.target : e.target.closest('[data-selection]');
		if (!closest) return selectComponent(null);
		childComp = findCompByIdRecursively(selectedPageObjectValue.pageData, closest.dataset.selection)?.comp;
	}
	dispatch(selectComponent({ props: comp ?? childComp, xPos, yPos, childComp }));
}

const CanvasElementsWM = ({ imgRef, placeholderProps, isDebugActive }) => {
	//redux
	const headerLayout = useSelector(headerPageObject).pageData;
	const footerLayout = useSelector(footerPageObject).pageData;
	const updateHover = _.debounce(handleHover, 50);
	const selectedCanvasResolution = useSelector(selectedToggleValue);
	const selectedPageObjectValue = useSelector(selectedPageObject);
	const pagesData = (selectedPageObjectValue && selectedPageObjectValue.pageData) || [];
	const dispatch = useDispatch();

	function handleHover(e) {
		if (e.ctrlKey) {
			const closest = e.target.dataset.selection ? e.target : e.target.closest('[data-selection]');

			if (closest) {
				const compId = closest.dataset.selection;
				const name = findCompByIdRecursively(selectedPageObjectValue.pageData, compId)?.comp?.componentName;
				if (!name) return clearHover();
				dispatch(setHoverElement({ componentId: closest.dataset.selection, componentName: name, isCanvas: true }));
			} else {
				clearHover();
			}
		} else {
			clearHover();
		}
	}
	function clearHover() {
		dispatch(setHoverElement({ componentId: null, componentName: null }));
	}

	const parentBoxProps = {
		flex: '1',
		ref: imgRef,
		alignSelf: 'center',
		w: selectedCanvasResolution > 1024 ? '100%' : selectedCanvasResolution + 'px',
		h: '100%',
		shadow: 'lg',
		order: 1,
		bg: 'white',
	};

	const droppableBoxProps = {
		w: '100%',
		minH: '300px',
		position: 'relative',
		className: 'canvas',
		overflowX: 'hidden',
		overflowY: 'auto',
		sx: scrollBarStyle,
	};

	const renderHeaderProps = {
		pagesData,
		headerLayout,
		selectedCanvasResolution,
		selectedPageObjectValue,
	};

	const renderFooterProps = {
		pagesData,
		footerLayout,
		selectedCanvasResolution,
		selectedPageObjectValue,
	};

	const draggableBoxProps = (dragProvided, snapshot, comp) => ({
		background: snapshot.isDragging ? '#fffee6' : 'initial',
		boxShadow: snapshot.isDragging ? '0 0 6px 2pt #ecc94b' : 'initial',
		transition: '.2s',
		role: 'group',
		position: 'relative',
		overflowY: 'visible',
		ref: dragProvided.innerRef,
		className: 'DraggableBox DraggableType-' + (comp.draggableType ? comp.draggableType : 'DEFAULT'),
		...dragProvided.draggableProps,
	});

	const draggablehHandleBoxProps = {
		d: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		className: 'DraggableHandle',
		bg: '#eee',
		borderRadius: '5px',
		h: '30px',
		w: '30px',
		sx: { ':hover': { bg: '#f6e05e', cursor: 'grab' }, ':active': { bg: '#ecc94b' } },
		zIndex: 999,
		top: '12px',
		right: '12px',
		position: 'absolute',
		_groupHover: { opacity: '1', visibility: 'visible' },
		opacity: 0,
		visibility: 'hidden',
		transition: '0.1s',
	};

	return (
		<Box {...parentBoxProps} className="teste">
			<Droppable droppableId={'CANVAS'} minH={'100%'} isDropDisabled={false}>
				{(provided, droppableSnapshot) => (
					<Box
						{...droppableBoxProps}
						h={isDebugActive ? '50%' : '100%'}
						{...provided.droppableProps}
						ref={provided.innerRef}
					>
						<RenderHeader {...renderHeaderProps} />
						{/**Teste para o placeholder */}
						<Box
							overflow={'visible'}
							position={'relative'}
							className="placeholder-container"
							onMouseOver={(e) => updateHover(e)}
						>
							{pagesData
								.filter((el) => filterByMobileState(el, selectedCanvasResolution))
								.map((comp, index, arr) => (
									<Draggable draggableId={comp.componentId} index={index} key={comp.componentId}>
										{(dragProvided, snapshot) => (
											<Box
												{...draggableBoxProps(dragProvided, snapshot, comp)}
												onContextMenu={(e) => {
													handleRightClick(e, selectedPageObjectValue, comp, dispatch);
												}}
											>
												<VtexComponent
													className="VtexComponent"
													{...{
														key: comp.componentId,
														componentId: comp.componentId,
														comp,
														componentGroup: comp.componentGroup,
													}}
												/>
												<Box {...draggablehHandleBoxProps} {...dragProvided.dragHandleProps}>
													<IconButton
														key={'icon-button-0'}
														variant="outline"
														size={'sm'}
														aria-label="Mover componente"
														sx={{ cursor: 'grab' }}
														icon={<UilDraggabledots size={18} />}
													/>
												</Box>
											</Box>
										)}
									</Draggable>
								))}
							{provided.placeholder}
							{!isEmpty(placeholderProps) && droppableSnapshot.isDraggingOver && (
								<CanvasElementPlaceholder {...placeholderProps} />
							)}
						</Box>
						<RenderFooter {...renderFooterProps} />
					</Box>
				)}
			</Droppable>
			{isDebugActive && process.env.NODE_ENV === 'development' ? <DebugBox /> : null}
			<HoverOverlay />
		</Box>
	);
};

const CanvasElements = React.memo(CanvasElementsWM);
export default CanvasElements;
